type LogLevel = 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
}

class Logger {
  private static instance: Logger;
  private logQueue: LogEntry[] = [];
  private readonly maxQueueSize = 100;
  private readonly logFile = 'logs/frontend.log';

  private constructor() {
    window.addEventListener('error', (event) => {
      this.error('Uncaught error:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled promise rejection:', {
        reason: event.reason
      });
    });
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private formatLogEntry(level: LogLevel, message: string, data?: any): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      data
    };
  }

  private async saveLog(entry: LogEntry) {
    this.logQueue.push(entry);
    console.log(`[${entry.level.toUpperCase()}] ${entry.message}`, entry.data || '');

    if (this.logQueue.length >= this.maxQueueSize) {
      await this.flushLogs();
    }
  }

  private async flushLogs() {
    if (this.logQueue.length === 0) return;

    const logsToSave = this.logQueue.splice(0, this.maxQueueSize);
    const logText = logsToSave
      .map(entry => `${entry.timestamp} [${entry.level}] ${entry.message} ${JSON.stringify(entry.data || '')}`)
      .join('\n') + '\n';

    // In a real application, you might want to send this to a backend API
    console.log('Flushing logs:', logText);
  }

  public info(message: string, data?: any) {
    this.saveLog(this.formatLogEntry('info', message, data));
  }

  public warn(message: string, data?: any) {
    this.saveLog(this.formatLogEntry('warn', message, data));
  }

  public error(message: string, data?: any) {
    this.saveLog(this.formatLogEntry('error', message, data));
  }
}

export const logger = Logger.getInstance(); 